import {Table} from 'notebook/tables/constants/TableConstants';
import {TABLE_COLUMN} from 'notebook/tables/constants/TableSelectionConstants';
import {TableHeaderRowProps} from 'notebook/tables/core-ui/TableHeaderRow';
import {RowFillDown} from 'notebook/tables/store/logic/RowFillDownLogic';
import TableUtil from 'notebook/tables/TableUtil';

export type AllowDeleteColumnParameters = {
  columnIndex: number;
  table: Table;
  canAddRemoveColumns: boolean;
} & Pick<TableHeaderRowProps, 'getSelectionRange'>;

/**
 * Determines if the selected column can be deleted from the table.
 *
 * Uses ColumnConstraint.isColumnUserConfigurable to determine if the column can be deleted. If that field is not
 * present defaults to checking table.legacyFirstEditableColumnIndex.
 *
 * @returns whether the column can be deleted
 */
export function allowDeleteColumn({
  columnIndex,
  canAddRemoveColumns,
  getSelectionRange,
  table,
}: AllowDeleteColumnParameters): boolean {
  if (!canAddRemoveColumns) {
    return false;
  }

  const range = getSelectionRange(TABLE_COLUMN, columnIndex);
  if (range == null) {
    return false;
  }

  // If isColumnUserConfigurable is set on any column constraints, it will be set on all of them, so we
  // will rely on it.
  let isColumnUserConfigurableSet = false;
  for (let i = range.minIndex; i <= range.maxIndex; i++) {
    const columnConstraint = table.columnConstraints?.[i];
    if (columnConstraint && columnConstraint.isColumnUserConfigurable != null) {
      isColumnUserConfigurableSet = true;
      if (!columnConstraint.isColumnUserConfigurable) {
        return false;
      }
    }
  }
  if (isColumnUserConfigurableSet) {
    return true;
  }

  // If isColumnUserConfigurable is not set (e.g. legacy results table), we fall back to the deprecated
  // legacyFirstEditableColumnIndex behavior
  const firstEditableIndex = TableUtil.legacyFirstEditableColumnIndex(table);
  return firstEditableIndex == null || (range != null && range.minIndex >= firstEditableIndex);
}

export {isCellSelected} from 'notebook/tables/core-ui/TableRowUtils';

export function cellFillProps(
  rowModel: RowFillDown,
  j: number
): {
  isFillDownBoxLeftEdge: boolean;
  isFillDownBoxRightEdge: boolean;
  isFillDownBoxTopEdge: boolean;
  isFillDownBoxBottomEdge: boolean;
} {
  return {
    isFillDownBoxLeftEdge:
      rowModel.isFilledDown && (rowModel.fillMinJ === j || j === (rowModel.fillMaxJ || 0) + 1),
    isFillDownBoxRightEdge:
      rowModel.isFilledDown && (rowModel.fillMaxJ === j || j === (rowModel.fillMinJ || 0) - 1),
    isFillDownBoxTopEdge:
      rowModel.isFillDownBoxTopEdge && (rowModel.fillMinJ || 0) <= j && j <= (rowModel.fillMaxJ || 0),
    isFillDownBoxBottomEdge:
      rowModel.isFillDownBoxBottomEdge && (rowModel.fillMinJ || 0) <= j && j <= (rowModel.fillMaxJ || 0),
  };
}
